import React, { useState, useEffect } from "react";
import {
  getTeamMembers,
  deleteTeamMember,
  reorderTeamMembers,
  TeamMember,
} from "../services/teamMembers";
import { getImageUrl } from "../services/api";
import "./TeamMemberList.css";

interface TeamMemberListProps {
  onEdit: (teamMember: TeamMember) => void;
  refreshTrigger?: number;
}

const TeamMemberList: React.FC<TeamMemberListProps> = ({
  onEdit,
  refreshTrigger,
}) => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draggedItem, setDraggedItem] = useState<number | null>(null);

  useEffect(() => {
    fetchTeamMembers();
  }, [refreshTrigger]);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTeamMembers(false); // Get all including inactive
      setTeamMembers(data);
    } catch (err) {
      setError("Failed to fetch team members");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (window.confirm(`Are you sure you want to delete ${name}?`)) {
      try {
        await deleteTeamMember(id);
        fetchTeamMembers();
      } catch (err) {
        alert("Failed to delete team member");
        console.error(err);
      }
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();

    if (draggedItem === null || draggedItem === index) return;

    const newMembers = [...teamMembers];
    const draggedMember = newMembers[draggedItem];

    newMembers.splice(draggedItem, 1);
    newMembers.splice(index, 0, draggedMember);

    setTeamMembers(newMembers);
    setDraggedItem(index);
  };

  const handleDragEnd = async () => {
    if (draggedItem === null) return;

    // Update display order on server
    const orders = teamMembers.map((member, index) => ({
      id: member.id,
      displayOrder: index,
    }));

    try {
      await reorderTeamMembers(orders);
    } catch (err) {
      console.error("Failed to update order:", err);
      fetchTeamMembers(); // Refresh to restore original order
    }

    setDraggedItem(null);
  };

  if (loading) {
    return (
      <div className="team-member-list">
        <div className="loading">Loading team members...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="team-member-list">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="team-member-list">
      <div className="list-header">
        <h2>Team Members ({teamMembers.length})</h2>
        <p className="drag-hint">💡 Drag and drop to reorder</p>
      </div>

      {teamMembers.length === 0 ? (
        <div className="empty-state">
          <p>No team members found. Add your first team member!</p>
        </div>
      ) : (
        <div className="team-member-grid">
          {teamMembers.map((member, index) => (
            <div
              key={member.id}
              className={`team-member-card ${
                !member.isActive ? "inactive" : ""
              }`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
            >
              <div className="card-drag-handle">
                <i className="fas fa-grip-vertical"></i>
              </div>

              <div className="card-image">
                <img
                  src={getImageUrl(member.photo)}
                  alt={member.name}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "/placeholder-avatar.png";
                  }}
                />
                {!member.isActive && (
                  <div className="inactive-badge">Inactive</div>
                )}
              </div>

              <div className="card-content">
                <h3>{member.name}</h3>
                <p className="position">{member.position}</p>
                <div className="card-meta">
                  <span className="display-order">
                    Order: {member.displayOrder}
                  </span>
                </div>
              </div>

              <div className="card-actions">
                <button
                  className="btn btn-edit"
                  onClick={() => onEdit(member)}
                  title="Edit"
                >
                  <i className="fas fa-edit"></i> Edit
                </button>
                <button
                  className="btn btn-delete"
                  onClick={() => handleDelete(member.id, member.name)}
                  title="Delete"
                >
                  <i className="fas fa-trash"></i> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamMemberList;
